import { useEffect, useState } from "react";
import { Form, Modal, Spinner } from "react-bootstrap";
import { dateToString, notNullToDate, toFloat } from "../../utilities/auxiliary-functions";
import { cpfMask, numberMask, phoneMask, removeFormatDate, removeMask, zipcodeMask } from "../../utilities/masks";
import { isCpf, isEmpty } from "../../utilities/validators";
import RuleBenefitRecord from "../records/rule-benefit.record";
import { FaTimes } from "react-icons/fa";
import { useAlertMessageContext } from "../../contexts/alert-message.context";
import { useConfirmMessageContext } from "../../contexts/confirm-message.context";
import { AlertMessageEnum } from "../../utilities/types";
import { Button } from '@mui/material';
import { openDocument } from "../../utilities/view-document";
import cduService from "../../services/cdu.service";
import { useTranslateContext } from "../../contexts/translate.context";
import { usePartnerContext } from "../../contexts/partner.context";
import bnfService from "../../services/bnf.service";
import authService from "../../services/auth.service";

export interface IRuleBenefitCadastreModalProps {
    isEdit: boolean,
    show: boolean,
    recordId: string,
    record: any,
    records: any,
    onSubmitModal: any,
    onCancelModal: any
}

export const RuleBenefitCadastreModal: React.FC<IRuleBenefitCadastreModalProps> = (props: IRuleBenefitCadastreModalProps) => {

    const alertContext = useAlertMessageContext();
    const confirmContext = useConfirmMessageContext();
    const { display } = useTranslateContext();
    let { partner } = usePartnerContext();

    const title : string = display.title.rule_benefit;

    const [ invalidatedCadastreForm, setInvalidatedCadastreForm ] = useState(false);
    const [ sending, setSending ] = useState(false);

    // Rule
    const [ id, setId ] = useState(0);
    const [ benefitId, setBenefitId ] = useState(0);
    const [ launch, setLaunch ] = useState(dateToString(new Date(), 'dd/mm/yyyy') + ' 00:00:00'); 
    const [ category, setCategory ] = useState(''); 
    const [ service, setService ] = useState(''); 
    const [ regulation, setRegulation ] = useState(''); 
    const [ cost, setCost ] = useState(0); 
    const [ amount, setAmount ] = useState('');
    const [ description, setDescription ] = useState('');
    const [ condition, setCondition ] = useState('');
    const [ initial, setInitial ] = useState('');
    const [ final, setFinal ] = useState('');
    const [ created, setCreated ] = useState('');
    const [ updated, setUpdated ] = useState('');

    const [ listBenefit, setListBenefit ] = useState([] as any);
    const [ listCategory, setListCategory ] = useState([] as any);
    const [ listService, setListService ] = useState([] as any);
    const [ listRegulation, setListRegulation ] = useState([] as any);
    const [ listCondition, setListCondition ] = useState([] as any);
    const [ listAmount, setListAmount ] = useState([] as any);

    async function isValidForm() {
        let result = true;
        let emptyRequired = false;

        if (isEmpty(regulation))
            emptyRequired = true;

        if (emptyRequired) {
            await alertContext.show(AlertMessageEnum.FAIL, title, display.message.invalid.required);
            result = false;
        }

        setInvalidatedCadastreForm(!result);
        return result;
    }

    async function onClick_Confirm(event : any) {
        event.preventDefault();

        const isValid = await isValidForm();
        if (isValid) {
            try {
                const isConfirmed = await confirmContext.show(title, display.message.confirm_record);
                if (isConfirmed) {
                    setSending(false);
                    const rule = fillRule();
                    if (rule.id > 0)
                        await bnfService.saveRule(rule.id, rule);
                    else
                        await bnfService.createRule(rule);

                    setSending(false);
                    await alertContext.show(AlertMessageEnum.SUCCESS, title, display.message.the_record_has_been_saved_successfully);
                    props.onSubmitModal(event, rule);
                }
            } catch (error: any) {
                setSending(false);
                await alertContext.show(AlertMessageEnum.FAIL, title, error);
            }
        }
    }

    async function onClick_Cancel(event : any) {
        event.preventDefault();

        setInvalidatedCadastreForm(false);
        props.onCancelModal(event);
    }

    function clearRule() {
        setId(0);
        setBenefitId(0);
        setLaunch(dateToString(new Date(), 'dd/mm/yyyy'));
        setCategory('');
        setService('');
        setRegulation('');
        setCost(0);
        setAmount('');
        setDescription('');
        setCondition('');
        setInitial('');
        setFinal('');
        setCreated('');
        setUpdated('');
    }

    function fillDataForm(rule: any) {

        if (rule != null) {
            setId(rule.id);
            setBenefitId(rule.benefitId);
            setLaunch(notNullToDate(rule.launch, 'dd/mm/yyyy'));
            setCategory(rule.category);
            setService(rule.service);
            setRegulation(rule.regulation);
            setCost(rule.cost);
            setAmount(rule.amount);
            setDescription(rule.description);
            setCondition(rule.condition);
            setInitial(notNullToDate(rule.initial, 'dd/mm/yyyy'));
            setFinal(notNullToDate(rule.final, 'dd/mm/yyyy'));
            setCreated(notNullToDate(rule.created, 'dd/mm/yyyy HH:MM:ss'));
            setUpdated(notNullToDate(rule.updated, 'dd/mm/yyyy HH:MM:ss'));
        }
    }

    function fillRule() {

        const dataRule = {
            id,
            benefitId,
            launch: removeFormatDate(launch),
            category,
            service,
            regulation,
            cost,
            amount,
            description,
            condition,
            initial: removeFormatDate(initial),
            final: removeFormatDate(final),
            created: removeFormatDate(created), 
            updated: removeFormatDate(updated)
        };

        return dataRule;
    }

    async function initialize() {
        if (props.isEdit) {
            fillDataForm(props.record);
        } else {
            clearRule();
        }
    }

    async function changeListCategory() {
        const attributes = ['id', 'codiname', 'category']
        const listBenefit = await bnfService.benefitList(attributes);
        setListBenefit(listBenefit.rows);

        const list = await bnfService.presetListCategory();
        setListCategory(list.rows);
        const listCondition = await bnfService.presetListCondition();
        setListCondition(listCondition.rows);
    }

    async function changeListService() {
        const listService = await bnfService.presetListService(category);
        setListService(listService.rows);
        const listRegulation = await bnfService.presetListRegulation(category);
        setListRegulation(listRegulation.rows);
        const listAmount = await bnfService.presetListAmount(category);
        setListAmount(listAmount.rows);
    }

    useEffect(() => {
        changeListService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category]);

    useEffect(() => {
        if (isEmpty(partner))
            // eslint-disable-next-line react-hooks/exhaustive-deps
            partner = authService.currentPartner();

        if (props.show) {
            changeListCategory();
            initialize();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.show]);

    function viewCadastreForm() {
        return (
            <>
                <Modal show={props.show}
                    size="lg"
                    aria-labelledby="modal-cadastre-rule-form"
                    centered
                >
                    <Modal.Header>
                        <Modal.Title id="modal-cadastre-rule-form">{title}</Modal.Title>
                        <FaTimes className="isClickable" onClick={ onClick_Cancel } size={26}/>
                    </Modal.Header>
                    <Modal.Body>
                        <Form className="form" id="form-cadastre-rule" validated={invalidatedCadastreForm}>
                            <RuleBenefitRecord
                                title={title}
                                isEdit={true}
                                identifier={id === 0}
                                
                                listBenefit={listBenefit}
                                listCategory={listCategory}
                                listService={listService}
                                listRegulation={listRegulation}
                                listCondition={listCondition}
                                listAmount={listAmount}

                                created={created}
                                updated={updated}
                        
                                benefitId={benefitId}
                                launch={launch}
                                category={category}
                                service={service}
                                regulation={regulation}
                                cost={cost}
                                amount={amount}
                                description={description}
                                condition={condition}
                                initial={initial}
                                final={final}
                                setBenefitId={(value: any) => { setBenefitId(value); }}
                                setLaunch={(value: any) => { setLaunch(value); }}
                                setCategory={(value: any) => { setCategory(value); }}
                                setService={(value: any) => { setService(value); }}
                                setRegulation={(value: any) => { setRegulation(value); }}
                                setCost={(value: any) => { setCost(value); }}
                                setAmount={(value: any) => { setAmount(value); }}
                                setDescription={(value: any) => { setDescription(value); }}
                                setCondition={(value: any) => { setCondition(value); }}
                                setInitial={(value: any) => { setInitial(value); }}
                                setFinal={(value: any) => { setFinal(value); }}
                            />
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onClick={ onClick_Cancel } variant="contained" color="secondary">{display.buttom.cancel}</Button>
                        <Button  variant="contained" color="primary" onClick={ onClick_Confirm } disabled={sending}>
                            { sending ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : null }{'  '}
                            { display.buttom.confirm }
                        </Button>
                    </Modal.Footer>
                </Modal>
            </>
        )
    }

    return (
        <>
        { viewCadastreForm() }
        </>
    )
}

export default RuleBenefitCadastreModal
